import {
  normalize_realtime_event_data,
  to_positive_int,
  get_event_list,
  replace_event_in_list,
  update_event_holders,
  patch_event_in_all_lists,
  remove_event_from_list,
} from "./realtimeEventHelpers";

function decrement_number_field(obj, field_name, amount) {
  if (!obj || typeof obj !== "object") return obj;
  const current = Number(Reflect.get(obj, field_name));
  if (!Number.isFinite(current)) return obj;
  const next = Math.max(0, current - amount);
  if (next === current) return obj;
  return ({ ...(obj || {}), [field_name]: next });
}

function update_ticket_entry_availability(entry, ticket_id_num, qty_num) {
  const entry_ticket_id = Number(entry?.ticket?.id ?? entry?.event_ticket_id ?? entry?.ticket_id ?? entry?.id);
  if (entry_ticket_id !== ticket_id_num) return entry;

  let next_entry = entry;
  next_entry = decrement_number_field(next_entry, "available_instances", qty_num);
  next_entry = decrement_number_field(next_entry, "available", qty_num);
  next_entry = decrement_number_field(next_entry, "remaining", qty_num);
  return next_entry;
}

function get_event_tickets_list_or_null(event_obj) {
  const tickets = Reflect.get(event_obj || {}, "event_tickets");
  return Array.isArray(tickets) ? tickets : null;
}

function normalize_positive_number(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

function map_ticket_entries_with_change_flag(tickets, updater) {
  let changed = false;
  const next_tickets = tickets.map((entry) => {
    const next_entry = updater(entry);
    if (next_entry !== entry) changed = true;
    return next_entry;
  });
  return { next_tickets, changed };
}

function update_ticket_availability_in_event(event_obj, ticket_id, quantity) {
  if (!event_obj || typeof event_obj !== "object") return event_obj;
  const tickets = get_event_tickets_list_or_null(event_obj);
  if (!tickets) return event_obj;

  const ticket_id_num = normalize_positive_number(ticket_id);
  const qty_num = normalize_positive_number(quantity);
  if (!ticket_id_num || !qty_num) return event_obj;

  const { next_tickets, changed } = map_ticket_entries_with_change_flag(
    tickets,
    (entry) => update_ticket_entry_availability(entry, ticket_id_num, qty_num)
  );

  if (!changed) return event_obj;
  return ({ ...(event_obj || {}), event_tickets: next_tickets });
}

export function update_event_ticket_purchased_in_all_lists(view_model, event_id, purchase_data, list_names) {
  const ticket_id = to_positive_int(purchase_data?.ticket_id);
  const quantity = to_positive_int(purchase_data?.quantity);
  if (!ticket_id || !quantity) return false;

  const id_num = to_positive_int(event_id);
  if (!id_num) return false;

  const names = Array.isArray(list_names) ? list_names : ["upcoming_events", "my_events", "past_events"];
  let changed = false;
  const updater = (event_obj) => update_ticket_availability_in_event(event_obj, ticket_id, quantity);

  for (const name of names) {
    const list = get_event_list(view_model, name);
    changed = replace_event_in_list(list, id_num, updater) || changed;
  }

  changed = update_event_holders(view_model, id_num, updater) || changed;

  return changed;
}

function apply_ticket_purchase_to_ticket_entry(ticket_entry, ticket_id_num, qty_num) {
  if (!ticket_entry || typeof ticket_entry !== "object") return ticket_entry;
  const entry_ticket_id = get_ticket_entry_id(ticket_entry);
  if (entry_ticket_id !== ticket_id_num) return ticket_entry;

  return decrement_ticket_availability_fields(ticket_entry, qty_num);
}

function is_order_for_event(order_obj, event_id_num) {
  if (!order_obj || typeof order_obj !== "object") return false;
  const order_event_id = Number(order_obj?.event_id ?? order_obj?.event?.id ?? order_obj?.event?.event_id);
  return Boolean(order_event_id && order_event_id === event_id_num);
}

function apply_ticket_purchase_to_order_field(order_obj, field_name, ticket_id_num, qty_num) {
  const list = Reflect.get(order_obj || {}, field_name);
  if (!Array.isArray(list)) return { next_order: order_obj, changed: false };

  let changed = false;
  const next_list = list.map((entry) => {
    const updated = apply_ticket_purchase_to_ticket_entry(entry, ticket_id_num, qty_num);
    if (updated !== entry) changed = true;
    return updated;
  });

  if (!changed) return { next_order: order_obj, changed: false };
  return ({ next_order: { ...(order_obj || {}), [field_name]: next_list }, changed: true });
}

function apply_ticket_purchase_to_order(order_obj, event_id_num, ticket_id_num, qty_num) {
  if (!is_order_for_event(order_obj, event_id_num)) return order_obj;

  let next_order = { ...(order_obj || {}) };
  let changed = false;

  const event_ticket_update = apply_ticket_purchase_to_order_field(next_order, "event_tickets", ticket_id_num, qty_num);
  next_order = event_ticket_update.next_order;
  changed = event_ticket_update.changed || changed;

  const items_update = apply_ticket_purchase_to_order_field(next_order, "items", ticket_id_num, qty_num);
  next_order = items_update.next_order;
  changed = items_update.changed || changed;

  return changed ? next_order : order_obj;
}

function get_ticket_entry_id(ticket_entry) {
  return Number(
    ticket_entry?.ticket?.id ?? ticket_entry?.event_ticket_id ?? ticket_entry?.ticket_id ?? ticket_entry?.id
  );
}

function decrement_ticket_availability_fields(ticket_entry, qty_num) {
  let next_entry = ticket_entry;
  next_entry = decrement_number_field(next_entry, "available_instances", qty_num);
  next_entry = decrement_number_field(next_entry, "available", qty_num);
  next_entry = decrement_number_field(next_entry, "remaining", qty_num);
  return next_entry;
}

function get_realtime_payload_detail(dom_event) {
  const payload = dom_event?.detail;
  return payload && typeof payload === "object" ? payload : null;
}

function is_event_management_realtime_payload(payload) {
  return !(payload?.module && payload.module !== "event_management");
}

function get_realtime_payload_type(payload) {
  return payload?.event_type || payload?.type || null;
}

function get_ticket_purchase_payload_data(payload) {
  if (!is_event_management_realtime_payload(payload)) return null;
  if (get_realtime_payload_type(payload) !== "event_ticket_purchased") return null;

  const normalized = normalize_realtime_event_data(payload?.data);
  if (!normalized?.id) return null;

  const ticket_id_num = to_positive_int(normalized?.ticket_id);
  const qty_num = to_positive_int(normalized?.quantity);
  if (!ticket_id_num || !qty_num) return null;

  return ({
    event_id: Number(normalized.id),
    ticket_id_num,
    qty_num,
  });
}

export function handle_checkout_realtime_ticket_purchase(view_model, dom_event) {
  const payload = get_realtime_payload_detail(dom_event);
  if (!payload) return false;

  const purchase_data = get_ticket_purchase_payload_data(payload);
  if (!purchase_data || !view_model?.order) return false;

  const next_order = apply_ticket_purchase_to_order(
    view_model.order,
    purchase_data.event_id,
    purchase_data.ticket_id_num,
    purchase_data.qty_num
  );
  if (next_order === view_model.order) return false;
  view_model.order = next_order;
  return true;
}

function normalize_time_string(timeStr) {
  if (timeStr === null || timeStr === undefined) return "";
  let normalized_time = String(timeStr).trim();
  if (!normalized_time) return "";
  normalized_time = normalized_time.replace(/Z$/i, "");
  normalized_time = normalized_time.replace(/([+-]\d{2}:?\d{2})$/i, "");
  normalized_time = normalized_time.split(".")[0];
  if (/^\d{1,2}:\d{2}$/.test(normalized_time)) normalized_time = `${normalized_time}:00`;
  return normalized_time;
}

const TRUE_BOOL_VALUES = new Set(["true", "1", "yes", "y"]);
const FALSE_BOOL_VALUES = new Set(["false", "0", "no", "n"]);

function to_bool_from_number(value) {
  if (value === 1) return true;
  if (value === 0) return false;
  return null;
}

function to_bool_from_string(value) {
  if (!value) return null;
  if (TRUE_BOOL_VALUES.has(value)) return true;
  if (FALSE_BOOL_VALUES.has(value)) return false;
  return null;
}

function to_bool(value) {
  if (typeof value === "boolean") return value;

  const numeric_bool = to_bool_from_number(value);
  if (numeric_bool !== null) return numeric_bool;

  const raw = (value ?? "").toString().trim().toLowerCase();
  return to_bool_from_string(raw);
}

function is_supported_portal_publish_event_type(event_type) {
  return ["event_published_updated", "event_published", "event_unpublished"].includes(event_type);
}

function get_publish_toggle_payload_data(payload) {
  if (!is_event_management_realtime_payload(payload)) return null;

  const event_type = get_realtime_payload_type(payload);
  if (!event_type || !is_supported_portal_publish_event_type(event_type)) return null;

  const normalized = normalize_realtime_event_data(payload?.data);
  if (!normalized?.id) return null;

  if (normalized.is_published === null || normalized.is_published === undefined) return null;
  const is_published = to_bool(normalized.is_published);
  if (is_published === null) return null;

  return { normalized, is_published };
}

function parse_event_dt(dateStr, timeStr) {
  if (!dateStr) return null;
  const datePart = typeof dateStr === "string" ? dateStr.trim() : String(dateStr);

  const timePart = normalize_time_string(timeStr);
  if (timePart) {
    const dt = new Date(`${datePart}T${timePart}`);
    if (!Number.isNaN(dt.getTime())) return dt;
  }

  const fallback = new Date(datePart);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function insert_event_into_list_if_missing(view_model, list_name, event_id, event_obj) {
  const list = get_event_list(view_model, list_name);
  if (!list) return false;
  const id_num = Number(event_id);
  if (!id_num) return false;
  if (list.some((item) => Number(item?.id) === id_num)) return false;
  list.unshift({ ...(event_obj || {}), id: id_num });
  return true;
}

function find_event_in_list(view_model, list_name, event_id) {
  const list = get_event_list(view_model, list_name);
  if (!Array.isArray(list)) return null;
  return list.find((item) => Number(item?.id) === Number(event_id)) || null;
}

function remove_event_by_id(list, event_id) {
  return Array.isArray(list) ? list.filter((e) => Number(e?.id) !== Number(event_id)) : [];
}

function ensure_event_present(list, event_obj) {
  const safe_list = Array.isArray(list) ? list : [];
  const exists = safe_list.some((e) => Number(e?.id) === Number(event_obj?.id));
  return exists ? safe_list : [event_obj, ...safe_list];
}

function get_owner_id_from_event(event_obj) {
  return (
    Number(event_obj?.user?.id) ||
    Number(event_obj?.created_by?.id) ||
    Number(event_obj?.creator?.id) ||
    0
  );
}

export function apply_portal_event_publish_state(view_model, event_obj, is_published) {
  if (!view_model || !event_obj?.id) return false;
  const event_id = event_obj.id;

  if (is_published) {
    // Published => visible in `events`, removed from drafts `my_events`.
    view_model.my_events = remove_event_by_id(view_model.my_events, event_id);
    view_model.events = ensure_event_present(view_model.events, event_obj);
    view_model.upsert_calendar_event?.(event_obj);
    return true;
  }

  // Unpublished => removed from published list for everyone.
  view_model.events = remove_event_by_id(view_model.events, event_id);

  // Only the creator/owner should see unpublished events in drafts.
  const owner_id = get_owner_id_from_event(event_obj);
  if (owner_id && Number(view_model.user_id) === owner_id) {
    view_model.my_events = ensure_event_present(view_model.my_events, event_obj);
    view_model.upsert_calendar_event?.(event_obj);
    return true;
  }

  // Ensure it doesn't appear for other users.
  view_model.my_events = remove_event_by_id(view_model.my_events, event_id);
  view_model.remove_calendar_event?.(event_id);
  return true;
}

export function handle_portal_events_realtime_publish_toggle(view_model, dom_event) {
  const payload = get_realtime_payload_detail(dom_event);
  if (!payload) return false;

  const publish_data = get_publish_toggle_payload_data(payload);
  if (!publish_data) return false;

  return apply_portal_event_publish_state(view_model, publish_data.normalized, publish_data.is_published);
}

function get_is_published_from_payload(published_data) {
  if (published_data?.is_published === null || published_data?.is_published === undefined) return null;
  return to_bool(published_data.is_published);
}

function build_merged_published_event(view_model, event_id, published_data, is_published) {
  const cached_event =
    find_event_in_list(view_model, "my_events", event_id) ||
    find_event_in_list(view_model, "upcoming_events", event_id) ||
    find_event_in_list(view_model, "past_events", event_id);

  return {
    ...(cached_event || {}),
    ...(published_data || {}),
    id: Number(event_id),
    is_published,
  };
}

function has_publish_card_fields(event_obj) {
  return Boolean(event_obj?.event_name || event_obj?.start_date || event_obj?.location);
}

function apply_unpublished_list_removals(view_model, event_id) {
  remove_event_from_list(view_model, "upcoming_events", event_id);
  remove_event_from_list(view_model, "past_events", event_id);
  return true;
}

function upsert_published_event_into_time_list(view_model, event_id, merged_event) {
  const end_dt =
    parse_event_dt(merged_event?.end_date, merged_event?.end_time) ||
    parse_event_dt(merged_event?.start_date, merged_event?.start_time);

  const is_past = !!(end_dt && end_dt.getTime() < Date.now());
  if (is_past) {
    insert_event_into_list_if_missing(view_model, "past_events", event_id, merged_event);
    remove_event_from_list(view_model, "upcoming_events", event_id);
    return true;
  }

  insert_event_into_list_if_missing(view_model, "upcoming_events", event_id, merged_event);
  remove_event_from_list(view_model, "past_events", event_id);
  return true;
}

export function update_event_published_in_all_lists(view_model, event_id, published_data, list_names) {
  const is_published = get_is_published_from_payload(published_data);
  if (is_published === null) return false;

  // Always patch existing cards first (no-op if the event isn't loaded in-memory).
  patch_event_in_all_lists(view_model, event_id, { is_published }, list_names);

  // View_Events "upcoming" (and typically "past") lists are fetched with `is_published=true`,
  // so when an event is unpublished we must remove it from those lists immediately.
  if (!is_published) return apply_unpublished_list_removals(view_model, event_id);

  // When an event is (re)published, it may not exist in-memory yet (e.g. it was previously unpublished).
  // If the realtime payload includes enough fields to render a card, insert it into the right list.
  remove_event_from_list(view_model, "my_events", event_id);

  const merged_event = build_merged_published_event(view_model, event_id, published_data, is_published);
  if (!has_publish_card_fields(merged_event)) return true;
  return upsert_published_event_into_time_list(view_model, event_id, merged_event);
}

function has_any_event_card_fields(event_obj) {
  if (!event_obj || typeof event_obj !== "object") return false;
  const fields = ["start_date", "start_time", "location", "event_type"];
  return fields.some((field) => {
    const value = Reflect.get(event_obj, field);
    return value !== null && value !== undefined && value !== "";
  });
}

function get_full_event_from_payload(payload_data) {
  if (!payload_data || typeof payload_data !== "object") return null;
  if (payload_data.event && typeof payload_data.event === "object") return payload_data.event;
  if (payload_data.event_data && typeof payload_data.event_data === "object") return payload_data.event_data;
  return null;
}

export function can_insert_created_event(payload_data, normalized_patch) {
  // The cards currently render fields like start_date/start_time/location/event_type, etc.
  // With only { event_id, title, is_published } we cannot safely insert an event.
  if (!normalized_patch?.event_name) return false;

  const full_event = get_full_event_from_payload(payload_data);
  if (has_any_event_card_fields(full_event)) return true;
  return has_any_event_card_fields(payload_data);
}

// NOTE: snake_case exports are the supported API for this module:
// - `update_event_ticket_purchased_in_all_lists`
// - `handle_checkout_realtime_ticket_purchase`
// - `handle_portal_events_realtime_publish_toggle`
// - `update_event_published_in_all_lists`
// - `can_insert_created_event`
